import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ClipboardPaste,
  Loader2,
  Check,
  ShoppingBag,
  AlertCircle,
} from "lucide-react";

import AppHeader from "@/components/AppHeader";
import WizardSteps, { type WizardStepMeta } from "@/components/wizard/WizardSteps";
import SuccessCelebration from "@/components/card/SuccessCelebration";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { createCollection } from "@/services/collections";
import { getActiveTemplateColumns } from "@/services/templates";
import { cn } from "@/lib/utils";

/** Shape of the data collected across the 4 product-pool steps.
 *  Kept intentionally flat (rather than nested per-step objects) so
 *  persisting it via `setState` is straightforward and individual
 *  inputs can update their own field without spreading nested state. */
interface ProductPoolData {
  productUrl: string;
  productName: string;
  productPrice: string;
  productImage: string;
  productStore: string;

  occasion: string;
  title: string;
  messageToGroup: string;
  deadlineDays: number;

  shippingName: string;
  shippingAddress: string;
  shippingCity: string;
  shippingPhone: string;

  templateId: string;
  templateUrl: string;
}

interface Template {
  id: string;
  name: string;
  image_url: string;
  occasion: string;
  price: number | null;
}

const initialData: ProductPoolData = {
  productUrl: "",
  productName: "",
  productPrice: "",
  productImage: "",
  productStore: "",
  occasion: "",
  title: "",
  messageToGroup: "",
  deadlineDays: 14,
  shippingName: "",
  shippingAddress: "",
  shippingCity: "",
  shippingPhone: "",
  templateId: "",
  templateUrl: "",
};

/** Derive a human-readable store name from a URL hostname. Strips
 *  the leading "www." and the TLD so "mumzworld.com" renders as
 *  "Mumzworld". Falls back to the raw host on parse failure. */
const deriveStoreName = (url: string): string => {
  try {
    const host = new URL(url).hostname.replace(/^www\./i, "");
    const [first] = host.split(".");
    return first ? first.charAt(0).toUpperCase() + first.slice(1) : host;
  } catch {
    return "";
  }
};

const SUGGESTED_STORES = ["Mumzworld", "Noon", "Amazon", "IKEA", "Namshi"];
const DEADLINE_OPTIONS = [7, 14, 21, 30];

/** Occasion catalogue shared with the money/sign-only wizard's
 *  StepOccasion — kept in sync so card-view heading logic (which
 *  branches on the `occasion` string) behaves identically for both
 *  pool types. Emojis match the ones StepOccasion uses. */
const OCCASIONS: { value: string; label: string; emoji: string }[] = [
  { value: "birthday", label: "Birthday", emoji: "🎂" },
  { value: "farewell", label: "Farewell", emoji: "👋" },
  { value: "baby", label: "New baby", emoji: "👶" },
  { value: "wedding", label: "Wedding", emoji: "💍" },
  { value: "thank-you", label: "Thank you", emoji: "🙏" },
  { value: "retirement", label: "Retirement", emoji: "🎉" },
  { value: "get-well", label: "Get well", emoji: "💐" },
  { value: "sympathy", label: "Sympathy", emoji: "🕊️" },
  { value: "graduation", label: "Graduation", emoji: "🎓" },
  { value: "teacher", label: "Teacher", emoji: "📚" },
  { value: "anniversary", label: "Anniversary", emoji: "❤️" },
  { value: "congratulations", label: "Congratulations", emoji: "🏆" },
];

/* ================================================================
 * Step 1 — Product URL
 *
 * User pastes a product URL. We call the existing
 * `extract-url-metadata` edge function (the same one the wishlist /
 * gift-me flows use) to scrape the title/price/image. On failure we
 * fall back to a manual entry form so the flow is never blocked on
 * flaky scraping.
 * ============================================================= */
interface StepProps {
  data: ProductPoolData;
  update: (patch: Partial<ProductPoolData>) => void;
}

const StepProduct = ({ data, update }: StepProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [url, setUrl] = useState(data.productUrl);
  const [loading, setLoading] = useState(false);
  const [manual, setManual] = useState(false);

  const productConfirmed =
    !!data.productName && !!data.productPrice && !!data.productUrl;

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setUrl(text.trim());
        void extractFromUrl(text.trim());
      }
    } catch {
      /* clipboard API might be blocked — user can still type the URL */
    }
  };

  const extractFromUrl = async (rawUrl: string) => {
    const trimmed = rawUrl.trim();
    if (!trimmed) return;
    setLoading(true);
    setManual(false);
    try {
      const { data: meta, error } = await supabase.functions.invoke(
        "extract-url-metadata",
        { body: { url: trimmed } },
      );
      if (error) throw error;

      if (!meta?.title && !meta?.price && !meta?.image) {
        // Scraper returned nothing useful — offer manual entry
        setManual(true);
        update({ productUrl: trimmed });
        toast({
          title: t("productPool.scrapeFailed", {
            defaultValue: "Couldn't fetch product",
          }),
          description: t("productPool.scrapeFailedDesc", {
            defaultValue: "Enter the product details manually below.",
          }),
        });
        return;
      }

      update({
        productUrl: trimmed,
        productName: meta.title ?? "",
        productPrice: meta.price != null ? String(meta.price) : "",
        productImage: meta.image ?? "",
        productStore: deriveStoreName(trimmed),
      });
    } catch {
      setManual(true);
      update({ productUrl: trimmed });
      toast({
        variant: "destructive",
        title: t("productPool.scrapeFailed", {
          defaultValue: "Couldn't fetch product",
        }),
        description: t("productPool.scrapeFailedDesc", {
          defaultValue: "Enter the product details manually below.",
        }),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setUrl("");
    setManual(false);
    update({
      productUrl: "",
      productName: "",
      productPrice: "",
      productImage: "",
      productStore: "",
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground font-serif mb-1">
          {t("productPool.product.title", {
            defaultValue: "Paste the product link",
          })}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("productPool.product.subtitle", {
            defaultValue: "Works with any online store",
          })}
        </p>
      </div>

      {/* URL input row with a paste shortcut. Enter triggers extract. */}
      <div className="flex gap-2">
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && url.trim()) {
              e.preventDefault();
              void extractFromUrl(url);
            }
          }}
          onBlur={() => {
            if (url.trim() && url.trim() !== data.productUrl) {
              void extractFromUrl(url);
            }
          }}
          placeholder="https://mumzworld.com/..."
          className="flex-1"
          disabled={loading}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={handlePaste}
          disabled={loading}
          aria-label={t("productPool.product.paste", { defaultValue: "Paste" })}
        >
          <ClipboardPaste size={16} />
        </Button>
      </div>

      {/* Store suggestion chips — visual hint that any store works */}
      <div className="flex flex-wrap gap-2">
        {SUGGESTED_STORES.map((name) => (
          <span
            key={name}
            className="text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground"
          >
            {name}
          </span>
        ))}
      </div>

      {loading && (
        <div className="rounded-xl border border-border p-6 flex items-center justify-center gap-3 text-sm text-muted-foreground">
          <Loader2 size={16} className="animate-spin" />
          {t("productPool.product.fetching", {
            defaultValue: "Fetching product details...",
          })}
        </div>
      )}

      {!loading && productConfirmed && !manual && (
        <div className="rounded-xl border border-border p-5 space-y-4">
          {data.productImage ? (
            <img
              src={data.productImage}
              alt={data.productName}
              className="w-full max-h-64 object-contain rounded-lg bg-muted"
            />
          ) : (
            <div className="w-full h-40 rounded-lg bg-muted flex items-center justify-center">
              <ShoppingBag className="text-muted-foreground" size={32} />
            </div>
          )}
          <div className="space-y-1">
            <p className="font-semibold text-foreground">{data.productName}</p>
            {data.productStore && (
              <p className="text-sm text-muted-foreground">
                {t("productPool.product.fromStore", {
                  defaultValue: "from {{store}}",
                  store: data.productStore,
                })}
              </p>
            )}
            <p className="text-lg font-bold text-foreground">
              AED {Number(data.productPrice).toLocaleString()}
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
            <Check size={16} />
            {t("productPool.product.confirmed", {
              defaultValue: "Product confirmed",
            })}
          </div>
          <button
            type="button"
            onClick={handleReset}
            className="text-xs text-primary hover:underline"
          >
            {t("productPool.product.wrongProduct", {
              defaultValue: "Wrong product? Paste a different URL",
            })}
          </button>
        </div>
      )}

      {/* Manual fallback — shown either when scraping fails or when
          the user explicitly chose to skip scraping. Required fields
          gate the Next button via canNext() in the parent. */}
      {(manual || (data.productUrl && !productConfirmed && !loading)) && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-50/40 dark:bg-amber-500/5 p-5 space-y-4">
          <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <p>
              {t("productPool.product.manualIntro", {
                defaultValue:
                  "Couldn't fetch automatically. Enter details manually:",
              })}
            </p>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">
                {t("productPool.product.nameLabel", {
                  defaultValue: "Product name",
                })}
              </Label>
              <Input
                value={data.productName}
                onChange={(e) => update({ productName: e.target.value })}
                placeholder="e.g. UPPAbaby Vista V2 Stroller"
              />
            </div>
            <div>
              <Label className="text-xs">
                {t("productPool.product.priceLabel", {
                  defaultValue: "Price (AED)",
                })}
              </Label>
              <Input
                type="number"
                inputMode="decimal"
                value={data.productPrice}
                onChange={(e) => update({ productPrice: e.target.value })}
                placeholder="3499"
              />
            </div>
            <div>
              <Label className="text-xs">
                {t("productPool.product.imageLabel", {
                  defaultValue: "Product image URL (optional)",
                })}
              </Label>
              <Input
                value={data.productImage}
                onChange={(e) => update({ productImage: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ================================================================
 * Step 2 — Details (title, message, deadline)
 * ============================================================= */
const StepProductDetails = ({ data, update }: StepProps) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground font-serif mb-1">
          {t("productPool.details.title", {
            defaultValue: "Tell contributors what this is for",
          })}
        </h2>
      </div>

      <div>
        <Label className="text-sm font-medium mb-1.5 block">
          {t("productPool.details.occasionLabel", {
            defaultValue: "Occasion",
          })}
          <span className="text-destructive ms-0.5">*</span>
        </Label>
        <div className="grid grid-cols-2 gap-2">
          {OCCASIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => update({ occasion: o.value })}
              className={cn(
                "flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all text-start",
                data.occasion === o.value
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-foreground hover:border-foreground/30",
              )}
            >
              <span className="text-base">{o.emoji}</span>
              {t(`occasions.${o.value === "baby" ? "newBaby" : o.value === "thank-you" ? "thankYou" : o.value === "get-well" ? "getWell" : o.value}`, { defaultValue: o.label })}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-sm font-medium mb-1.5 block">
          {t("productPool.details.titleLabel", { defaultValue: "Title" })}
          <span className="text-destructive ms-0.5">*</span>
        </Label>
        <Input
          value={data.title}
          onChange={(e) => update({ title: e.target.value.slice(0, 60) })}
          placeholder={t("productPool.details.titlePlaceholder", {
            defaultValue: "e.g., Sarah's Baby Shower Gift",
          })}
          maxLength={60}
        />
        <p className="text-xs text-muted-foreground mt-1">
          {data.title.length}/60
        </p>
      </div>

      <div>
        <Label className="text-sm font-medium mb-1.5 block">
          {t("productPool.details.messageLabel", {
            defaultValue: "Message to contributors",
          })}
        </Label>
        <Textarea
          value={data.messageToGroup}
          onChange={(e) =>
            update({ messageToGroup: e.target.value.slice(0, 280) })
          }
          placeholder={t("productPool.details.messagePlaceholder", {
            defaultValue:
              "Tell friends why you're pooling for this gift...",
          })}
          maxLength={280}
          rows={4}
        />
        <p className="text-xs text-muted-foreground mt-1">
          {data.messageToGroup.length}/280
        </p>
      </div>

      <div>
        <Label className="text-sm font-medium mb-1.5 block">
          {t("productPool.details.deadlineLabel", {
            defaultValue: "Pool deadline",
          })}
          <span className="text-destructive ms-0.5">*</span>
        </Label>
        <Select
          value={String(data.deadlineDays)}
          onValueChange={(v) => update({ deadlineDays: Number(v) })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DEADLINE_OPTIONS.map((days) => (
              <SelectItem key={days} value={String(days)}>
                {days} {t("productPool.details.days", { defaultValue: "days" })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

/* ================================================================
 * Step 3 — Recipient (shipping details)
 * ============================================================= */
const StepRecipient = ({ data, update }: StepProps) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-foreground font-serif mb-1">
        {t("productPool.recipient.title", {
          defaultValue: "Where should we deliver?",
        })}
      </h2>

      <div>
        <Label className="text-sm font-medium mb-1.5 block">
          {t("productPool.recipient.name", {
            defaultValue: "Recipient name",
          })}
          <span className="text-destructive ms-0.5">*</span>
        </Label>
        <Input
          value={data.shippingName}
          onChange={(e) => update({ shippingName: e.target.value })}
          placeholder="Sarah Ahmed"
        />
      </div>

      <div>
        <Label className="text-sm font-medium mb-1.5 block">
          {t("productPool.recipient.address", {
            defaultValue: "Street address",
          })}
          <span className="text-destructive ms-0.5">*</span>
        </Label>
        <Input
          value={data.shippingAddress}
          onChange={(e) => update({ shippingAddress: e.target.value })}
          placeholder="123 Marina Walk, Apt 4B"
        />
      </div>

      <div>
        <Label className="text-sm font-medium mb-1.5 block">
          {t("productPool.recipient.city", { defaultValue: "City" })}
          <span className="text-destructive ms-0.5">*</span>
        </Label>
        <Input
          value={data.shippingCity}
          onChange={(e) => update({ shippingCity: e.target.value })}
          placeholder="Dubai"
        />
      </div>

      <div>
        <Label className="text-sm font-medium mb-1.5 block">
          {t("productPool.recipient.phone", {
            defaultValue: "Phone (for delivery)",
          })}
          <span className="text-destructive ms-0.5">*</span>
        </Label>
        <Input
          type="tel"
          value={data.shippingPhone}
          onChange={(e) => update({ shippingPhone: e.target.value })}
          placeholder="+971 50 123 4567"
        />
      </div>
    </div>
  );
};

/* ================================================================
 * Step 4 — Design (template picker + live preview)
 *
 * Reuses the same `card_templates` rows as the money-pool flow so
 * the organiser sees a consistent catalogue. We cap the grid at 6
 * templates (shuffled by the DB's default order) — product pools
 * don't need the full filter UI since the design is contextual.
 * ============================================================= */
const StepProductDesign = ({ data, update }: StepProps) => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    getActiveTemplateColumns().then(({ data: rows }) => {
      if (!active) return;
      // Prefer free templates first so the cheapest option is the
      // default visual pick — paid templates still appear but after.
      const sorted = [...rows].sort((a, b) => {
        const pa = a.price ?? 0;
        const pb = b.price ?? 0;
        return pa - pb;
      });
      setTemplates(sorted.slice(0, 6));
      if (!data.templateId && sorted[0]) {
        update({ templateId: sorted[0].id, templateUrl: sorted[0].image_url });
      }
      setLoading(false);
    });
    return () => {
      active = false;
    };
    // Mount-only — we auto-pick the first template on initial load
    // but don't want to re-run when the parent's `update` identity
    // changes or when data.templateId is later edited by the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedTemplate =
    templates.find((t) => t.id === data.templateId) ?? templates[0];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-foreground font-serif mb-1">
        {t("productPool.design.title", {
          defaultValue: "Choose a card design",
        })}
      </h2>

      {loading ? (
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="aspect-[3/4] rounded-lg bg-muted animate-pulse"
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {templates.map((tmpl) => {
            const isSelected = data.templateId === tmpl.id;
            return (
              <button
                key={tmpl.id}
                type="button"
                onClick={() =>
                  update({ templateId: tmpl.id, templateUrl: tmpl.image_url })
                }
                className={cn(
                  "relative aspect-[3/4] rounded-lg overflow-hidden border-2 transition-all",
                  isSelected
                    ? "border-primary shadow-md"
                    : "border-transparent hover:border-border",
                )}
              >
                <img
                  src={tmpl.image_url}
                  alt={tmpl.name}
                  className="w-full h-full object-cover"
                />
                {isSelected && (
                  <div className="absolute top-1.5 end-1.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                    <Check size={12} className="text-primary-foreground" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Live preview — composes the template background with the
          product image and the organiser's copy so they can see
          what contributors will land on. */}
      <div>
        <Label className="text-sm font-medium mb-2 block">
          {t("productPool.design.preview", { defaultValue: "Preview" })}
        </Label>
        <div className="rounded-xl overflow-hidden border border-border bg-card">
          {selectedTemplate && (
            <div
              className="aspect-[4/3] w-full bg-cover bg-center relative"
              style={{ backgroundImage: `url(${selectedTemplate.image_url})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/50 to-black/80" />
              <div className="absolute inset-0 flex flex-col justify-end p-5 text-white">
                <h3 className="text-lg font-bold font-serif">
                  {data.title || t("productPool.design.titleFallback", {
                    defaultValue: "Your pool title",
                  })}
                </h3>
                {data.messageToGroup && (
                  <p className="text-sm opacity-90 line-clamp-2 mt-1">
                    {data.messageToGroup}
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="p-4 flex gap-3 items-center">
            {data.productImage && (
              <img
                src={data.productImage}
                alt={data.productName}
                className="w-16 h-16 rounded-lg object-cover bg-muted shrink-0"
              />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground truncate">
                {data.productName || "—"}
              </p>
              {data.productPrice && (
                <p className="text-sm text-muted-foreground">
                  AED {Number(data.productPrice).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================================================================
 * Orchestrator page — step state machine, footer nav, and create
 * ============================================================= */
const CreateProductPool = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();

  const [step, setStep] = useState(0);
  const [data, setData] = useState<ProductPoolData>(initialData);
  const [creating, setCreating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdSlug, setCreatedSlug] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth?next=/create/product");
    }
  }, [authLoading, user, navigate]);

  const update = (patch: Partial<ProductPoolData>) =>
    setData((prev) => ({ ...prev, ...patch }));

  const steps: WizardStepMeta[] = useMemo(
    () => [
      { key: "product", label: t("productPool.steps.product", { defaultValue: "Product" }) },
      { key: "details", label: t("productPool.steps.details", { defaultValue: "Details" }) },
      { key: "recipient", label: t("productPool.steps.recipient", { defaultValue: "Recipient" }) },
      { key: "design", label: t("productPool.steps.design", { defaultValue: "Design" }) },
    ],
    [t],
  );
  const currentKey = steps[step]?.key;

  const canNext = (): boolean => {
    switch (currentKey) {
      case "product":
        // Scraped or manually-entered: need name + price to proceed
        return (
          !!data.productName.trim() &&
          !!data.productPrice.trim() &&
          Number(data.productPrice) > 0
        );
      case "details":
        return (
          !!data.title.trim() &&
          !!data.occasion &&
          data.deadlineDays > 0
        );
      case "recipient":
        return (
          !!data.shippingName.trim() &&
          !!data.shippingAddress.trim() &&
          !!data.shippingCity.trim() &&
          !!data.shippingPhone.trim()
        );
      case "design":
        return !!data.templateId;
      default:
        return false;
    }
  };

  const handleBack = () => {
    if (step === 0) {
      navigate("/create");
      return;
    }
    setStep((s) => s - 1);
  };

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep((s) => s + 1);
    } else {
      void handleCreate();
    }
  };

  const handleCreate = async () => {
    if (!user) return;
    setCreating(true);

    const deadlineIso = new Date(
      Date.now() + data.deadlineDays * 24 * 60 * 60 * 1000,
    ).toISOString();
    const price = Number(data.productPrice);

    // Product pools piggyback on `collections` with pool_type='product'
    // and reuse the existing contribution plumbing. We store the
    // product price into collection_goal_amount so the shared
    // progress bar / goal UI on the card view works unchanged.
    const { data: created, error } = await createCollection({
      organizer_id: user.id,
      type: "card_collection",
      pool_type: "product",
      occasion: data.occasion,
      title: data.title,
      recipient_name: data.shippingName,
      message_to_group: data.messageToGroup || null,
      deadline: deadlineIso,
      card_template_id: data.templateId || null,
      collection_enabled: true,
      contribution_type: "any_amount",
      fee_handling: "deduct_from_collection",
      collection_goal: true,
      collection_goal_amount: price,
      show_total_collected: true,
      status: "active",
      product_url: data.productUrl,
      product_name: data.productName,
      product_price: price,
      product_image: data.productImage || null,
      product_store: data.productStore || null,
      shipping_name: data.shippingName,
      shipping_address: data.shippingAddress,
      shipping_city: data.shippingCity,
      shipping_phone: data.shippingPhone,
      order_status: "pending",
    });

    if (error || !created) {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: error?.message || "Could not create pool",
      });
      setCreating(false);
      return;
    }

    setCreatedSlug(created.share_slug);
    setCreating(false);
    setShowSuccess(true);
  };

  if (!authLoading && !user) return null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AnimatePresence>
        {showSuccess && createdSlug && (
          <SuccessCelebration
            type="card"
            onDone={() => navigate(`/card/${createdSlug}`)}
          />
        )}
      </AnimatePresence>

      <AppHeader onBack="/create" />

      <div className="border-b border-border/60 bg-background/80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <WizardSteps
            steps={steps}
            currentStep={step}
            onStepClick={(i) => i < step && setStep(i)}
          />
        </div>
      </div>

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 pb-32">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              {currentKey === "product" && (
                <StepProduct data={data} update={update} />
              )}
              {currentKey === "details" && (
                <StepProductDetails data={data} update={update} />
              )}
              {currentKey === "recipient" && (
                <StepRecipient data={data} update={update} />
              )}
              {currentKey === "design" && (
                <StepProductDesign data={data} update={update} />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      <div className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-sm border-t border-border">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={creating}
            className="shrink-0"
          >
            <ArrowLeft size={16} className="me-1.5" />
            {t("createCard.back", { defaultValue: "Back" })}
          </Button>

          <div className="flex-1" />

          <Button
            onClick={handleNext}
            disabled={creating || !canNext()}
            size="lg"
            className="rounded-full min-w-[8rem]"
          >
            {step === steps.length - 1
              ? creating
                ? t("productPool.creating", { defaultValue: "Creating..." })
                : t("productPool.createPool", { defaultValue: "Create Pool" })
              : t("createCard.next", { defaultValue: "Next" })}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateProductPool;
